/**
 * track.js — Tiny analytics helper. Fire-and-forget.
 *
 * Design:
 *  • No cookies. Session identity comes from sessionStorage.
 *  • Honours DNT at the server side (this file doesn't even check it —
 *    the backend silently drops events from DNT clients).
 *  • Never throws. Network failures, JSON errors, missing session —
 *    all silent. Analytics must never break the app.
 */

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const SESSION_KEY = "os_analytics_session";

function getSessionId() {
    try {
        let sid = sessionStorage.getItem(SESSION_KEY);
        if (!sid) {
            // 12 random base36 chars — enough for per-tab identity,
            // doesn't cross tabs or browser restarts.
            sid = Math.random().toString(36).slice(2, 14);
            sessionStorage.setItem(SESSION_KEY, sid);
        }
        return sid;
    } catch {
        return null;
    }
}

export function track(event, props = {}) {
    try {
        const body = JSON.stringify({
            event,
            session_id: getSessionId(),
            props: {
                // Merge caller props first, then ensure path/referrer
                // are always present (overridable by explicit props).
                path: window.location.pathname,
                referrer: document.referrer || undefined,
                ...props,
            },
        });
        // Use sendBeacon when possible — fire-and-forget, works during
        // page unload (crucial for page_view events on rapid navigation).
        const url = `${BACKEND}/api/analytics/event`;
        if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: "application/json" });
            if (navigator.sendBeacon(url, blob)) return;
        }
        // Fallback: standard fetch, no await, no error handling.
        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body,
            credentials: "include",
            keepalive: true,
        }).catch(() => {});
    } catch {
        // Silent — analytics must never break the app.
    }
}

export function trackPageView() {
    track("page_view");
}
